import { useEffect, useMemo, useState, type ReactNode, type SelectHTMLAttributes } from 'react';
import { api, type Member, type Sharing, type ShareAccessLevel, type ShareMode, type User } from '../api/client';
import { Avatar } from './AppShell';
import posthog from '../lib/posthog';

interface ShareDialogProps {
  artifactId: string;
  open: boolean;
  members: Member[];
  artifactAuthor: User;
  onClose: () => void;
}

const ACCESS_OPTIONS: ShareAccessLevel[] = ['viewer', 'commenter', 'editor'];

function formatAccessLabel(value: string) {
  return value[0].toUpperCase() + value.slice(1);
}

function isValidEmail(text: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(text);
}

interface InlineSelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  children: ReactNode;
}

function InlineSelect({ children, className = '', style, ...props }: InlineSelectProps) {
  return (
    <div className="relative">
      <select
        {...props}
        className={`h-[36px] min-w-[128px] appearance-none rounded-[8px] border bg-transparent px-[var(--sp-3)] pr-[36px] text-[13px] leading-none font-semibold ${className}`}
        style={{
          background: 'var(--rd-surface-input)',
          borderColor: 'var(--rd-border)',
          color: 'var(--rd-text-strong)',
          ...style,
        }}
      >
        {children}
      </select>
      <i
        className="icon icon-app-angle-down pointer-events-none absolute right-[10px] top-1/2 -translate-y-1/2"
        style={{ fontSize: 14, color: 'var(--rd-text-meta)' }}
      />
    </div>
  );
}

export default function ShareDialog({ artifactId, open, members, artifactAuthor, onClose }: ShareDialogProps) {
  const [sharing, setSharing] = useState<Sharing | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [search, setSearch] = useState('');
  const [inviteAccessLevel, setInviteAccessLevel] = useState<ShareAccessLevel>('viewer');

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setSearch('');
    api.getArtifactSharing(artifactId)
      .then((response) => setSharing(response.sharing))
      .finally(() => setLoading(false));
  }, [artifactId, open]);

  const explicitInvites = useMemo(
    () => (sharing?.invites ?? []).filter((invite) => invite.user_id !== artifactAuthor.id),
    [artifactAuthor.id, sharing],
  );

  const invitedAccess = useMemo(
    () => new Map(explicitInvites.map((invite) => [invite.user_id, invite.access_level])),
    [explicitInvites],
  );

  const normalizedSearch = search.trim().toLowerCase();
  const searchResults = useMemo(() => {
    if (!normalizedSearch) return [];
    return members
      .filter((member) => member.id !== artifactAuthor.id)
      .filter((member) => !invitedAccess.has(member.id))
      .filter((member) => {
        const haystack = `${member.user.name} ${member.user.email}`.toLowerCase();
        return haystack.includes(normalizedSearch);
      })
      .slice(0, 6);
  }, [artifactAuthor.id, invitedAccess, members, normalizedSearch]);

  const showEmailInviteOption = useMemo(() => {
    if (!normalizedSearch || !isValidEmail(normalizedSearch)) return false;
    const matchesMember = members.some((m) => m.user.email.toLowerCase() === normalizedSearch);
    const alreadyInvited = explicitInvites.some((inv) => inv.user.email.toLowerCase() === normalizedSearch);
    const alreadyPending = (sharing?.pending_invites ?? []).some((p) => p.email.toLowerCase() === normalizedSearch);
    return !matchesMember && !alreadyInvited && !alreadyPending;
  }, [normalizedSearch, members, explicitInvites, sharing]);

  if (!open) return null;

  const canManage = !!sharing?.can_manage;

  const updateSettings = async (next: {
    mode?: ShareMode;
    workspace_access_level?: ShareAccessLevel | null;
    public_access_level?: ShareAccessLevel | null;
  }) => {
    if (!sharing || !canManage) return;
    setSaving(true);
    try {
      const response = await api.updateArtifactSharing(artifactId, {
        mode: next.mode ?? sharing.settings.mode,
        workspace_access_level:
          next.workspace_access_level !== undefined
            ? next.workspace_access_level
            : sharing.settings.workspace_access_level,
        public_access_level:
          next.public_access_level !== undefined
            ? next.public_access_level
            : sharing.settings.public_access_level,
      });
      if (next.mode && next.mode !== sharing.settings.mode) {
        posthog.capture('artifact_sharing_mode_changed', { artifact_id: artifactId, sharing_mode: next.mode, previous_mode: sharing.settings.mode });
      }
      setSharing(response.sharing);
    } finally {
      setSaving(false);
    }
  };

  const updateInvite = async (memberId: string, value: string) => {
    if (!sharing || !canManage) return;
    setSaving(true);
    try {
      const response = value
        ? await api.upsertArtifactInvite(artifactId, memberId, value as ShareAccessLevel)
        : await api.deleteArtifactInvite(artifactId, memberId);
      setSharing(response.sharing);
      if (value) {
        setSearch('');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleInviteByEmail = async (email: string) => {
    if (!sharing || !canManage) return;
    setSaving(true);
    try {
      const response = await api.inviteByEmail(artifactId, email, inviteAccessLevel);
      posthog.capture('artifact_invite_sent', { artifact_id: artifactId, access_level: inviteAccessLevel });
      setSharing(response.sharing);
      setSearch('');
    } finally {
      setSaving(false);
    }
  };

  const copyLink = async () => {
    if (!sharing) return;
    await navigator.clipboard.writeText(sharing.share_link);
    posthog.capture('artifact_share_link_copied', { artifact_id: artifactId, sharing_mode: sharing.settings.mode });
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
      <div
        className="absolute inset-0"
        style={{ background: 'rgba(0,0,0,0.3)' }}
        onClick={onClose}
      />
      <div
        className="relative z-10 w-[680px] max-w-[calc(100vw-32px)] max-h-[calc(100vh-32px)] overflow-y-auto rounded-[12px] border"
        style={{
          background: 'var(--rd-surface-panel)',
          borderColor: 'var(--rd-border)',
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
        }}
      >
        <div
          className="flex items-center justify-between px-[var(--sp-4)] py-[var(--sp-3)] border-b"
          style={{ borderColor: 'var(--rd-border)' }}
        >
          <div>
            <p
              className="m-0 text-[16px] font-semibold"
              style={{ color: 'var(--rd-text-strong)' }}
            >
              Share artifact
            </p>
            <p
              className="m-0 mt-[2px] text-[13px]"
              style={{ color: 'var(--rd-text-subtle)' }}
            >
              Choose who can open this artifact and what they can do.
            </p>
          </div>
          <div className="flex items-center gap-[var(--sp-2)]">
            <button
              onClick={copyLink}
              disabled={!sharing}
              className="h-[36px] px-[var(--sp-3)] rounded-[8px] text-[13px] font-semibold cursor-pointer border whitespace-nowrap disabled:opacity-60 disabled:cursor-default"
              style={{
                background: 'var(--rd-surface-hover)',
                borderColor: 'var(--rd-border)',
                color: 'var(--rd-accent)',
              }}
            >
              {copied ? 'Copied' : 'Get link'}
            </button>
            <button
              onClick={onClose}
              className="w-[32px] h-[32px] rounded-[8px] border-0 bg-transparent cursor-pointer transition-colors duration-[120ms]"
              style={{ color: 'var(--rd-text-meta)' }}
              onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--rd-text-strong)')}
              onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--rd-text-meta)')}
            >
              <i className="icon icon-app-close" />
            </button>
          </div>
        </div>

        {loading || !sharing ? (
          <div className="p-[var(--sp-6)] text-[13px]" style={{ color: 'var(--rd-text-subtle)' }}>
            Loading sharing settings...
          </div>
        ) : (
          <div className="p-[var(--sp-4)] flex flex-col gap-[var(--sp-5)]">
            {!canManage && (
              <div
                className="rounded-[8px] p-[var(--sp-3)] text-[13px]"
                style={{
                  background: 'var(--rd-surface-hover)',
                  color: 'var(--rd-text-medium)',
                }}
              >
                You can copy the share link, but only the artifact creator or workspace owners can change permissions.
              </div>
            )}

            <section className="flex flex-col gap-[var(--sp-3)]">
              <p className="m-0 text-[13px] font-semibold" style={{ color: 'var(--rd-text-strong)' }}>
                Sharing mode
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-[var(--sp-2)]">
                {(['restricted', 'workspace', 'public'] as ShareMode[]).map((mode) => (
                  <button
                    key={mode}
                    disabled={!canManage || saving}
                    onClick={() => updateSettings({ mode })}
                    className="rounded-[8px] border px-[var(--sp-3)] py-[var(--sp-3)] text-left cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                    style={{
                      borderColor:
                        sharing.settings.mode === mode
                          ? 'var(--rd-text-placeholder)'
                          : 'var(--rd-border)',
                      background:
                        sharing.settings.mode === mode
                          ? 'var(--rd-surface-highlight)'
                          : 'var(--rd-surface-panel)',
                    }}
                  >
                    <div className="text-[13px] font-semibold" style={{ color: 'var(--rd-text-strong)' }}>
                      {mode === 'restricted' ? 'Restricted' : mode === 'workspace' ? 'Workspace' : 'Public'}
                    </div>
                    <div className="mt-[4px] text-[13px]" style={{ color: 'var(--rd-text-subtle)' }}>
                      {mode === 'restricted'
                        ? 'Only invited workspace users can open it.'
                        : mode === 'workspace'
                          ? 'All workspace members get the default access.'
                          : 'Anyone with the link can open it.'}
                    </div>
                  </button>
                ))}
              </div>
            </section>

            {sharing.settings.mode !== 'restricted' && (
              <section className="grid grid-cols-1 sm:grid-cols-2 gap-[var(--sp-4)]">
                <label className="flex flex-col gap-[var(--sp-2)]">
                  <span className="text-[13px] font-semibold" style={{ color: 'var(--rd-text-strong)' }}>
                    Workspace default access
                  </span>
                  <InlineSelect
                    disabled={!canManage || saving}
                    value={sharing.settings.workspace_access_level ?? 'viewer'}
                    onChange={(e) => updateSettings({ workspace_access_level: e.target.value as ShareAccessLevel })}
                    className="h-[40px]"
                  >
                    {ACCESS_OPTIONS.map((option) => (
                      <option key={option} value={option}>
                        {option[0].toUpperCase() + option.slice(1)}
                      </option>
                    ))}
                  </InlineSelect>
                </label>

                {sharing.settings.mode === 'public' && (
                  <label className="flex flex-col gap-[var(--sp-2)]">
                    <span className="text-[13px] font-semibold" style={{ color: 'var(--rd-text-strong)' }}>
                      Public default access
                    </span>
                    <InlineSelect
                      disabled={!canManage || saving}
                      value={sharing.settings.public_access_level ?? 'viewer'}
                      onChange={(e) => updateSettings({ public_access_level: e.target.value as ShareAccessLevel })}
                      className="h-[40px]"
                    >
                      {ACCESS_OPTIONS.map((option) => (
                        <option key={option} value={option}>
                          {option[0].toUpperCase() + option.slice(1)}
                        </option>
                      ))}
                    </InlineSelect>
                    {sharing.settings.public_access_level === 'editor' && (
                      <span className="text-[13px]" style={{ color: 'var(--rd-text-subtle)' }}>
                        Anonymous visitors still top out at commenting. Editing remains signed-in workspace-only.
                      </span>
                    )}
                  </label>
                )}
              </section>
            )}

            {canManage && (
              <section className="flex flex-col gap-[var(--sp-3)]">
                <div className="flex items-center justify-between gap-[var(--sp-3)]">
                  <div>
                    <p className="m-0 text-[13px] font-semibold" style={{ color: 'var(--rd-text-strong)' }}>
                      Invite people
                    </p>
                    <p className="m-0 mt-[2px] text-[13px]" style={{ color: 'var(--rd-text-subtle)' }}>
                      Search by name or type an email address to invite someone.
                    </p>
                  </div>
                  <div className="flex items-center gap-[var(--sp-2)]">
                    <InlineSelect
                      disabled={saving}
                      value={inviteAccessLevel}
                      onChange={(e) => setInviteAccessLevel(e.target.value as ShareAccessLevel)}
                    >
                      {ACCESS_OPTIONS.map((option) => (
                        <option key={option} value={option}>
                          {formatAccessLabel(option)}
                        </option>
                      ))}
                    </InlineSelect>
                  </div>
                </div>
                <div className="flex flex-col gap-[var(--sp-2)]">
                  <input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search by name or email"
                    className="h-[40px] rounded-[8px] border px-[var(--sp-3)] outline-none transition-colors duration-[120ms]"
                    style={{
                      background: 'var(--rd-surface-input)',
                      borderColor: 'var(--rd-border)',
                      color: 'var(--rd-text-strong)',
                    }}
                  />
                  {normalizedSearch && (
                    <div
                      className="rounded-[8px] border overflow-hidden"
                      style={{ borderColor: 'var(--rd-border)' }}
                    >
                      {searchResults.map((member) => (
                        <div
                          key={member.id}
                          className="flex items-center gap-[var(--sp-3)] px-[var(--sp-4)] py-[var(--sp-3)] border-b last:border-b-0"
                          style={{ borderColor: 'var(--rd-border-subtle)' }}
                        >
                          <Avatar name={member.user.name} avatarUrl={member.user.avatar_url} size={28} />
                          <div className="flex-1 min-w-0">
                            <div className="text-[13px] font-semibold" style={{ color: 'var(--rd-text-strong)' }}>
                              {member.user.name}
                            </div>
                            <div className="text-[13px] truncate" style={{ color: 'var(--rd-text-subtle)' }}>
                              {member.user.email}
                            </div>
                          </div>
                          <button
                            disabled={saving}
                            onClick={() => updateInvite(member.id, inviteAccessLevel)}
                            className="h-[32px] px-[var(--sp-3)] rounded-[8px] text-[13px] font-semibold cursor-pointer border-0 text-white"
                            style={{ background: 'var(--rd-text-strong)' }}
                          >
                            Add
                          </button>
                        </div>
                      ))}
                      {showEmailInviteOption && (
                        <div
                          className="flex items-center gap-[var(--sp-3)] px-[var(--sp-4)] py-[var(--sp-3)] border-b last:border-b-0"
                          style={{ borderColor: 'var(--rd-border-subtle)' }}
                        >
                          <div
                            className="flex items-center justify-center rounded-full flex-shrink-0"
                            style={{ width: 28, height: 28, background: 'var(--rd-surface-hover)' }}
                          >
                            <i className="icon icon-communication-email" style={{ fontSize: 14, color: 'var(--rd-text-meta)' }} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-[13px] font-semibold" style={{ color: 'var(--rd-text-strong)' }}>
                              Invite {search.trim()}
                            </div>
                            <div className="text-[13px]" style={{ color: 'var(--rd-text-subtle)' }}>
                              Send an invite via email
                            </div>
                          </div>
                          <button
                            disabled={saving}
                            onClick={() => handleInviteByEmail(search.trim())}
                            className="h-[32px] px-[var(--sp-3)] rounded-[8px] text-[13px] font-semibold cursor-pointer border-0 text-white"
                            style={{ background: 'var(--rd-text-strong)' }}
                          >
                            Invite
                          </button>
                        </div>
                      )}
                      {searchResults.length === 0 && !showEmailInviteOption && (
                        <div className="px-[var(--sp-4)] py-[var(--sp-3)] text-[13px]" style={{ color: 'var(--rd-text-subtle)' }}>
                          No members matched. Enter a full email address to invite someone new.
                        </div>
                      )}
                    </div>
                  )}
                  {!normalizedSearch && (
                    <div className="text-[13px]" style={{ color: 'var(--rd-text-subtle)' }}>
                      Start typing a name or enter an email to invite someone.
                    </div>
                  )}
                </div>
              </section>
            )}

            <section className="flex flex-col gap-[var(--sp-3)]">
              <div className="flex items-center justify-between">
                <p className="m-0 text-[13px] font-semibold" style={{ color: 'var(--rd-text-strong)' }}>
                  People with direct access
                </p>
              </div>

              <div
                className="rounded-[8px] border overflow-hidden"
                style={{
                  borderColor: 'var(--rd-border)',
                }}
              >
                <div
                  className="flex items-center gap-[var(--sp-3)] px-[var(--sp-4)] py-[var(--sp-3)] border-b"
                  style={{ borderColor: 'var(--rd-border-subtle)' }}
                >
                  <Avatar name={artifactAuthor.name} avatarUrl={artifactAuthor.avatar_url} size={28} />
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] font-semibold" style={{ color: 'var(--rd-text-strong)' }}>
                      {artifactAuthor.name}
                    </div>
                    <div className="text-[13px] truncate" style={{ color: 'var(--rd-text-subtle)' }}>
                      {artifactAuthor.email}
                    </div>
                  </div>
                  <div
                    className="inline-flex items-center h-[32px] px-[var(--sp-3)] rounded-[var(--radius-full)] text-[13px] font-semibold"
                    style={{
                      background: 'var(--rd-surface-highlight)',
                      color: 'var(--rd-text-medium)',
                    }}
                    title="Artifact author has owner access and can delete this artifact."
                  >
                    Owner
                  </div>
                </div>
                {(sharing?.pending_invites ?? []).map((pending) => (
                  <div
                    key={pending.email}
                    className="flex items-center gap-[var(--sp-3)] px-[var(--sp-4)] py-[var(--sp-3)] border-b last:border-b-0"
                    style={{ borderColor: 'var(--rd-border-subtle)' }}
                  >
                    <div
                      className="flex items-center justify-center rounded-full flex-shrink-0"
                      style={{ width: 28, height: 28, background: 'var(--rd-surface-hover)' }}
                    >
                      <i className="icon icon-communication-email" style={{ fontSize: 14, color: 'var(--rd-text-meta)' }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] font-semibold" style={{ color: 'var(--rd-text-strong)' }}>
                        {pending.email}
                      </div>
                      <div className="text-[13px]" style={{ color: 'var(--rd-text-subtle)' }}>
                        Pending sign-up
                      </div>
                    </div>
                    <div
                      className="inline-flex items-center h-[32px] px-[var(--sp-3)] rounded-[var(--radius-full)] text-[13px] font-semibold"
                      style={{
                        background: 'var(--rd-surface-hover)',
                        color: 'var(--rd-text-medium)',
                      }}
                    >
                      {formatAccessLabel(pending.access_level)}
                    </div>
                  </div>
                ))}
                {explicitInvites.length > 0 ? explicitInvites.map((invite) => (
                  <div
                    key={invite.user_id}
                    className="flex items-center gap-[var(--sp-3)] px-[var(--sp-4)] py-[var(--sp-3)] border-b last:border-b-0"
                    style={{ borderColor: 'var(--rd-border-subtle)' }}
                  >
                    <Avatar name={invite.user.name} avatarUrl={invite.user.avatar_url} size={28} />
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] font-semibold" style={{ color: 'var(--rd-text-strong)' }}>
                        {invite.user.name}
                      </div>
                      <div className="text-[13px] truncate" style={{ color: 'var(--rd-text-subtle)' }}>
                        {invite.user.email}
                      </div>
                    </div>
                    {canManage ? (
                      <div className="flex items-center gap-[var(--sp-2)]">
                        <InlineSelect
                          disabled={saving}
                          value={invite.access_level}
                          onChange={(e) => updateInvite(invite.user_id, e.target.value)}
                        >
                          {ACCESS_OPTIONS.map((option) => (
                            <option key={option} value={option}>
                              {formatAccessLabel(option)}
                            </option>
                          ))}
                        </InlineSelect>
                        <button
                          onClick={() => updateInvite(invite.user_id, '')}
                          disabled={saving}
                          className="h-[36px] px-[var(--sp-3)] rounded-[8px] text-[13px] font-semibold cursor-pointer border"
                          style={{
                            background: 'transparent',
                            borderColor: 'var(--rd-border)',
                            color: 'var(--rd-text-medium)',
                          }}
                        >
                          Remove
                        </button>
                      </div>
                    ) : (
                      <div
                        className="inline-flex items-center h-[32px] px-[var(--sp-3)] rounded-[var(--radius-full)] text-[13px] font-semibold"
                        style={{
                          background: 'var(--rd-surface-hover)',
                          color: 'var(--rd-text-medium)',
                        }}
                      >
                        {formatAccessLabel(invite.access_level)}
                      </div>
                    )}
                  </div>
                )) : (
                  <div className="px-[var(--sp-4)] py-[var(--sp-3)] text-[13px]" style={{ color: 'var(--rd-text-subtle)' }}>
                    No additional people have direct access yet.
                  </div>
                )}
              </div>
            </section>
          </div>
        )}
      </div>
    </div>
  );
}
